import type {CandidateEvaluationAPI} from 'src/types/candidate-evaluation';
import type {
  CandidateEvaluationWithRecruiterHubAPI,
  CandidateEvaluationWithRecruiterHubState,
  ValidationsWithRecruiterHub,
  ValidationsWithoutResumeWithRecruiterHub,
} from 'src/types/candidate-evaluation-with-recruiter-hub';
import {
  normalizeNodeSchedule,
  parseNodeSchedule,
} from 'src/utils/node-schedule';
import {
  normalizeUtmConfig,
  getDefaultUtmConfigState,
} from 'src/utils/utm-config';


type NormalizeProps = {
  workflowId: string;
} & CandidateEvaluationWithRecruiterHubState;

export const normalizeCandidateEvaluationWithRecruiterHub = (
  payload: NormalizeProps,
): CandidateEvaluationWithRecruiterHubAPI => {
  const {
    transcript,
    transcript_source,
    resume,
    job_id,
    job_title,
    job_description,
    recipient_entity_id,
    conversation_id,
  } = payload;

  const nodeSchedule = normalizeNodeSchedule(payload);
  const utmConfig = normalizeUtmConfig(getDefaultUtmConfigState());
  const taskParams = {
    job_id,
    job_title,
    job_description,
    resume,
    transcript,
    transcript_source,
    recipient_entity_id,
    conversation_id,
  } as const;

  const finalParams = Object.keys(taskParams).reduce<Record<string, any>>(
    (acc, key) => {
      // @ts-expect-error - dynamic key access
      if (taskParams[key]) {
        // @ts-expect-error - dynamic key access
        acc[key] = taskParams[key];
      }
      return acc;
    },
    {},
  );

  return {
    task: {
      params: {
        ...finalParams,
      },
    },
    rh_enabled: true,
    ...nodeSchedule,
    ...utmConfig,
  };
};

export const parseCandidateEvaluationWithRecruiterHub = (
  data: CandidateEvaluationAPI,
): CandidateEvaluationWithRecruiterHubState => {
  const params = data.task.params;
  const {
    job_id,
    job_title,
    job_description,
    resume,
    transcript,
    transcript_source,
    recipient_entity_id,
    conversation_id,
  } = params;
  const nodeSchedule = parseNodeSchedule(data);

  return {
    job_id,
    job_title,
    job_description,
    resume,
    transcript,
    transcript_source,
    recipient_entity_id,
    conversation_id,
    rh_enabled: true,
    ...nodeSchedule,
  };
};

export const getErrorsWithRecruiterHub = (
  validations: ValidationsWithRecruiterHub,
): Array<string> => {
  const errors: Array<string> = [];
  const {
    jobIdEmpty,
    jobTitleEmpty,
    jobDescEmpty,
    additionalInputEmpty,
    previousNodeInputInvalid,
  } = validations || {};

  if (jobIdEmpty || jobTitleEmpty || jobDescEmpty) {
    errors.push('Please fill the required fields.');
  }
  if (additionalInputEmpty) {
    errors.push('Add at least one additional input (Previous Node / Resume).');
  }
  if (previousNodeInputInvalid) {
    errors.push('Only voiceflow nodes are allowed.');
  }
  return errors;
};

export const getErrorsWithoutResumeWithRecruiterHub = (
  validations: ValidationsWithoutResumeWithRecruiterHub,
): Array<string> => {
  const errors: Array<string> = [];
  const {
    jobIdEmpty,
    jobTitleEmpty,
    jobDescEmpty,
    previousNodeInputEmpty,
    previousNodeInputInvalid,
  } = validations || {};

  if (jobIdEmpty || jobTitleEmpty || jobDescEmpty) {
    errors.push('Please fill the required fields.');
  }
  if (previousNodeInputEmpty) {
    errors.push('Please fill previous node input.');
  }
  if (previousNodeInputInvalid) {
    errors.push('Only voiceflow nodes are allowed.');
  }
  return errors;
};
